import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Link2, Plus, Archive, Pencil, Trash2, ShieldCheck, GitBranch, CheckCircle2, List, Table } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { useAuth } from "@/context/AuthContext";
import { formatDateOnly } from "@/lib/dates";
import { EntityLinkManager } from "@/components/EntityLinkManager";

const VERIFY_COLOR = {
  unverified: "text-zinc-400", verified: "text-emerald-400", disputed: "text-amber-400", unable_to_verify: "text-zinc-500",
};
const IMPORTANCE_COLOR = {
  low: "text-zinc-500", medium: "text-sky-400", high: "text-amber-400", critical: "text-red-400",
};

const LINK_MAP = {
  source: { api: "/sources", label: (o) => o.title, title: "Sources" },
  claim: { api: "/claims", label: (o) => o.statement, title: "Claims & Evidence" },
  media: { api: "/media/assets", label: (o) => `${o.original_filename} (${o.visibility})`, title: "Media" },
  intel: { api: "/ghana-intel/records", label: (o) => `${o.title} (${o.status})`, title: "Ghana Intelligence" },
  claim_tracker: { api: "/claim-tracker/entries", label: (o) => `${o.speaker}: ${o.claim_summary || o.claim_text || ""}`, title: "Political Claims" },
  promise: { api: "/promise-tracker/promises", label: (o) => o.title, title: "Promises" },
};
const LINK_TYPES = ["source", "claim", "media", "intel", "claim_tracker", "promise"];

function Section({ title, children, action }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3"><h3 className="font-tactical font-bold text-sm">{title}</h3>{action}</div>
      {children}
    </Card>
  );
}

function evBlank() {
  return { title: "", event_date: "", end_date: "", date_precision: "exact", summary: "", notes: "", location: "", event_type: "", importance: "medium", order_index: 0 };
}

export default function TimelineDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canReview = user?.roles?.some((r) => ["super_admin", "editor"].includes(r));
  const [meta, setMeta] = useState(null);
  const [rec, setRec] = useState(null);
  const [evModal, setEvModal] = useState(null); // {mode:'create'|'edit', eid, form}
  const [linkModal, setLinkModal] = useState(null); // {eid, type, options, selected}
  const [verifyModal, setVerifyModal] = useState(null); // {ev}
  const [relatedModal, setRelatedModal] = useState(false);
  const [relatedOptions, setRelatedOptions] = useState([]);
  const [relatedSel, setRelatedSel] = useState([]);
  const [saving, setSaving] = useState(false);
  const [eventView, setEventView] = useState("chrono");

  const openEditEvent = (ev) => setEvModal({ mode: "edit", eid: ev.id, form: { title: ev.title, event_date: ev.event_date, end_date: ev.end_date || "", date_precision: ev.date_precision || "exact", summary: ev.summary || "", notes: ev.notes || "", location: ev.location || "", event_type: ev.event_type || "", importance: ev.importance || "medium", order_index: ev.order_index ?? 0 } });
  const linkCount = (ev) => LINK_TYPES.reduce((n, lt) => n + (ev.links?.[lt] || []).length, 0);

  const load = useCallback(() => {
    api.get(`/timeline/timelines/${id}`).then((r) => setRec(r.data)).catch(() => toast.error("Not found"));
  }, [id]);
  useEffect(() => {
    api.get("/timeline/meta").then((r) => setMeta(r.data)).catch(() => {});
    load();
  }, [load]);

  const saveField = async (patch, msg = "Saved") => {
    try { await api.patch(`/timeline/timelines/${id}`, patch); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setStatus = async (status) => {
    try { await api.post(`/timeline/timelines/${id}/status`, { status }); toast.success("Status updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const archive = async () => {
    try { await api.delete(`/timeline/timelines/${id}`); toast.success("Archived"); nav("/command-center/timelines"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submitEvent = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const f = evModal.form;
      if (!f.title || !f.event_date) { toast.error("Title and event date are required"); setSaving(false); return; }
      const payload = { ...f };
      payload.order_index = payload.order_index === "" ? 0 : Number(payload.order_index);
      Object.keys(payload).forEach((k) => { if (payload[k] === "" && k !== "order_index") delete payload[k]; });
      if (evModal.mode === "create") await api.post(`/timeline/timelines/${id}/events`, payload);
      else await api.patch(`/timeline/events/${evModal.eid}`, payload);
      toast.success(evModal.mode === "create" ? "Event added" : "Event updated");
      setEvModal(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const deleteEvent = async (eid) => {
    try { await api.delete(`/timeline/events/${eid}`); toast.success("Event deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const submitVerify = async (verification) => {
    try { await api.post(`/timeline/events/${verifyModal.ev.id}/verify`, { verification }); toast.success("Verification updated"); setVerifyModal(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openLink = async (eid, type) => {
    try {
      const { data } = await api.get(LINK_MAP[type].api);
      const ev = rec.events.find((e) => e.id === eid);
      const selected = (ev?.links?.[type] || []).map((x) => x.id);
      setLinkModal({ eid, type, options: data, selected });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveLinks = async () => {
    try {
      await api.post(`/timeline/events/${linkModal.eid}/links`, { link_type: linkModal.type, ids: linkModal.selected });
      toast.success("Links updated"); setLinkModal(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleSel = (v) => setLinkModal((m) => ({ ...m, selected: m.selected.includes(v) ? m.selected.filter((x) => x !== v) : [...m.selected, v] }));

  const openRelated = async () => {
    try {
      const { data } = await api.get("/timeline/timelines");
      setRelatedOptions(data.filter((t) => t.id !== id));
      setRelatedSel((rec.related_timelines || []).map((x) => x.id));
      setRelatedModal(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveRelated = async () => {
    try { await api.post(`/timeline/timelines/${id}/related`, { timeline_ids: relatedSel }); toast.success("Related timelines updated"); setRelatedModal(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleRelated = (v) => setRelatedSel((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v]);

  if (!rec) return <div className="p-6 text-zinc-500">Loading…</div>;

  return (
    <div className="p-6" data-testid="tl-detail">
      <button onClick={() => nav("/command-center/timelines")} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Timeline Builder</button>
      <PageHeader title={rec.title} subtitle={[rec.timeline_type, rec.topic, rec.region].filter(Boolean).join(" · ")}>
        {canReview && (
          <>
            <select data-testid="tl-status-select" className={`${inputCls} w-auto`} value={rec.status} onChange={(e) => setStatus(e.target.value)}>
              {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Btn variant="danger" data-testid="tl-archive-btn" onClick={archive}><Archive className="h-4 w-4" /> Archive</Btn>
          </>
        )}
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Overview">
            <Field label="Summary">
              <textarea data-testid="tl-summary-edit" className={inputCls} rows={2} defaultValue={rec.summary || ""}
                onBlur={(e) => e.target.value !== (rec.summary || "") && saveField({ summary: e.target.value }, "Summary saved")} />
            </Field>
            <Field label="Analyst notes">
              <textarea data-testid="tl-notes-edit" className={inputCls} rows={2} defaultValue={rec.analyst_notes || ""}
                onBlur={(e) => e.target.value !== (rec.analyst_notes || "") && saveField({ analyst_notes: e.target.value }, "Notes saved")} />
            </Field>
          </Section>

          <Section title={`Events (${rec.event_count})`}
            action={<div className="flex items-center gap-2">
              <div className="inline-flex rounded-sm border border-zinc-800 overflow-hidden" data-testid="tl-view-toggle">
                <button data-testid="tl-view-chrono" onClick={() => setEventView("chrono")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors ${eventView === "chrono" ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-zinc-300"}`}><List className="h-3.5 w-3.5" /> Chronological</button>
                <button data-testid="tl-view-compact" onClick={() => setEventView("compact")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors ${eventView === "compact" ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-zinc-300"}`}><Table className="h-3.5 w-3.5" /> Compact</button>
              </div>
              <Btn data-testid="tl-add-event" onClick={() => setEvModal({ mode: "create", form: evBlank() })}><Plus className="h-4 w-4" /> Add Event</Btn>
            </div>}>
            <p className="text-[0.7rem] text-zinc-600 mb-3">Ordered by event date, then manual order. Verification is never inferred from inclusion — marking an event verified requires a linked source or Claims &amp; Evidence record.</p>
            {rec.events.length === 0 ? <p className="text-xs text-zinc-600">No events yet. Add the first event to start the timeline.</p> : eventView === "chrono" ? (
              <ol className="relative border-l border-zinc-800 ml-2 space-y-5" data-testid="tl-events">
                {rec.events.map((ev) => (
                  <li key={ev.id} className="ml-4" data-testid={`tl-event-${ev.id}`}>
                    <span className={`absolute -left-1.5 h-3 w-3 rounded-full ${ev.verification === "verified" ? "bg-emerald-500" : "bg-zinc-600"}`} />
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono-data text-xs text-zinc-400">{formatDateOnly(ev.event_date)}{ev.end_date ? ` → ${formatDateOnly(ev.end_date)}` : ""}</span>
                          {ev.date_precision && ev.date_precision !== "exact" && <span className="text-[0.6rem] text-zinc-600 uppercase">({ev.date_precision})</span>}
                          {ev.event_type && <span className="text-[0.6rem] px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-400 uppercase">{ev.event_type}</span>}
                          <span className={`text-[0.6rem] uppercase font-semibold ${IMPORTANCE_COLOR[ev.importance] || "text-zinc-500"}`}>{ev.importance}</span>
                          <span data-testid={`tl-event-verify-${ev.id}`} className={`inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase ${VERIFY_COLOR[ev.verification]}`}>{ev.verification === "verified" && <CheckCircle2 className="h-3 w-3" />}{(ev.verification || "").replace(/_/g, " ")}</span>
                        </div>
                        <div className="text-sm font-medium text-zinc-100 mt-1">{ev.title}</div>
                        {ev.summary && <p className="text-sm text-zinc-400 mt-0.5">{ev.summary}</p>}
                        {ev.location && <p className="text-xs text-zinc-600 mt-0.5">{ev.location}</p>}
                        {ev.notes && <p className="text-xs text-zinc-500 mt-0.5 italic">{ev.notes}</p>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {LINK_TYPES.map((lt) => {
                        const items = ev.links?.[lt] || [];
                        return (
                          <button key={lt} data-testid={`tl-link-${lt}-${ev.id}`} onClick={() => openLink(ev.id, lt)}
                            className={`inline-flex items-center gap-1 text-[0.65rem] px-1.5 py-0.5 rounded-sm border transition-colors ${items.length > 0 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>
                            <Link2 className="h-3 w-3" /> {LINK_MAP[lt].title} {items.length > 0 && `(${items.length})`}
                          </button>
                        );
                      })}
                    </div>
                    {LINK_TYPES.some((lt) => (ev.links?.[lt] || []).length > 0) && (
                      <ul className="mt-2 space-y-0.5 text-xs text-zinc-400">
                        {LINK_TYPES.flatMap((lt) => (ev.links?.[lt] || []).map((it) => <li key={`${lt}-${it.id}`}>• <span className="text-zinc-600">{LINK_MAP[lt].title}:</span> {it.label}</li>))}
                      </ul>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button data-testid={`tl-edit-event-${ev.id}`} onClick={() => openEditEvent(ev)}
                        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-white"><Pencil className="h-3 w-3" /> Edit</button>
                      {canReview && <button data-testid={`tl-verify-btn-${ev.id}`} onClick={() => setVerifyModal({ ev })} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-emerald-400"><ShieldCheck className="h-3 w-3" /> Verify</button>}
                      <EntityLinkManager linkType="timeline_event" recordId={ev.id} variant="inline" />
                      <button data-testid={`tl-delete-event-${ev.id}`} onClick={() => deleteEvent(ev.id)} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-red-400"><Trash2 className="h-3 w-3" /> Delete</button>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm" data-testid="tl-events-compact">
                  <thead>
                    <tr className="text-left label-caps text-zinc-500 border-b border-zinc-800">
                      <th className="px-3 py-2">Date</th><th className="px-3 py-2">Event</th>
                      <th className="px-3 py-2">Type</th><th className="px-3 py-2">Importance</th>
                      <th className="px-3 py-2">Verification</th><th className="px-3 py-2">Evidence</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-900">
                    {rec.events.map((ev) => (
                      <tr key={ev.id} data-testid={`tl-crow-${ev.id}`} className="hover:bg-zinc-900/40 align-top">
                        <td className="px-3 py-2 font-mono-data text-xs text-zinc-400 whitespace-nowrap">{formatDateOnly(ev.event_date)}{ev.end_date ? ` → ${formatDateOnly(ev.end_date)}` : ""}</td>
                        <td className="px-3 py-2 max-w-xs"><div className="font-medium text-zinc-100 truncate">{ev.title}</div>{ev.location && <div className="text-xs text-zinc-600 truncate">{ev.location}</div>}</td>
                        <td className="px-3 py-2 text-xs text-zinc-400 uppercase">{ev.event_type || "—"}</td>
                        <td className={`px-3 py-2 text-xs uppercase ${IMPORTANCE_COLOR[ev.importance] || "text-zinc-500"}`}>{ev.importance}</td>
                        <td className={`px-3 py-2 text-xs uppercase ${VERIFY_COLOR[ev.verification]}`} data-testid={`tl-crow-verify-${ev.id}`}>{(ev.verification || "").replace(/_/g, " ")}</td>
                        <td className="px-3 py-2 text-xs text-zinc-400">{linkCount(ev)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2.5">
                            <button data-testid={`tl-crow-edit-${ev.id}`} onClick={() => openEditEvent(ev)} className="text-zinc-500 hover:text-white" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                            {canReview && <button data-testid={`tl-crow-verify-${ev.id}-btn`} onClick={() => setVerifyModal({ ev })} className="text-zinc-500 hover:text-emerald-400" title="Verify"><ShieldCheck className="h-3.5 w-3.5" /></button>}
                            <button data-testid={`tl-crow-link-${ev.id}`} onClick={() => openLink(ev.id, "source")} className="text-zinc-500 hover:text-sky-400" title="Link evidence"><Link2 className="h-3.5 w-3.5" /></button>
                            <EntityLinkManager linkType="timeline_event" recordId={ev.id} variant="inline" />
                            <button data-testid={`tl-crow-delete-${ev.id}`} onClick={() => deleteEvent(ev.id)} className="text-zinc-500 hover:text-red-400" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>

        <div className="space-y-4">
          <Section title="Details">
            <dl className="text-sm space-y-2">
              {[["Type", rec.timeline_type], ["Topic", rec.topic], ["Region", rec.region], ["Scope", rec.geo_scope],
                ["Start", formatDateOnly(rec.start_date)], ["End", formatDateOnly(rec.end_date)],
                ["Events", rec.event_count], ["Unverified", rec.unverified_count]].map(([k, v]) => (
                <div key={k}><dt className="label-caps text-zinc-500">{k}</dt><dd className="text-zinc-300">{(v === 0 || v) ? v : "—"}</dd></div>
              ))}
              <div><dt className="label-caps text-zinc-500">Tags</dt><dd className="text-zinc-300">{rec.tags?.length ? rec.tags.join(", ") : "—"}</dd></div>
            </dl>
          </Section>

          <Section title="Related Timelines" action={<Btn variant="ghost" data-testid="tl-related-btn" onClick={openRelated}><GitBranch className="h-4 w-4" /> Manage</Btn>}>
            {(rec.related_timelines || []).length === 0 ? <p className="text-xs text-zinc-600">No related timelines linked.</p> : (
              <ul className="space-y-1 text-sm" data-testid="tl-related-list">
                {rec.related_timelines.map((t) => (
                  <li key={t.id}><button onClick={() => nav(`/command-center/timelines/${t.id}`)} className="text-zinc-300 hover:text-white text-left">• {t.title} <span className="text-zinc-600 text-xs">({t.status})</span></button></li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="History (audit)">
            <ul className="space-y-2 text-xs" data-testid="tl-audit">
              {rec.audit_history.length === 0 && <li className="text-zinc-600">No activity.</li>}
              {rec.audit_history.map((a) => (
                <li key={a.id} className="text-zinc-400">
                  <span className="font-mono-data text-emerald-400">{a.action}</span>
                  {a.after?.status && <span> → {a.after.status}</span>}
                  {a.after?.verification && <span> → {a.after.verification}</span>}
                  {a.after?.event_date && <span> → {a.after.event_date}</span>}
                  <span className="text-zinc-600"> · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>

      <Modal open={!!evModal} onClose={() => setEvModal(null)} title={evModal?.mode === "create" ? "Add Event" : "Edit Event"} wide>
        {evModal && (
          <div className="space-y-4">
            <Field label="Event title"><input data-testid="tl-ev-title" className={inputCls} required value={evModal.form.title} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, title: e.target.value } })} /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Event date"><input type="date" data-testid="tl-ev-date" className={inputCls} value={evModal.form.event_date} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, event_date: e.target.value } })} /></Field>
              <Field label="End date (optional)"><input type="date" data-testid="tl-ev-enddate" className={inputCls} value={evModal.form.end_date} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, end_date: e.target.value } })} /></Field>
              <Field label="Date precision"><select data-testid="tl-ev-precision" className={inputCls} value={evModal.form.date_precision} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, date_precision: e.target.value } })}>{(meta?.date_precisions || []).map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Event type"><select data-testid="tl-ev-type" className={inputCls} value={evModal.form.event_type} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, event_type: e.target.value } })}><option value="">—</option>{(meta?.event_types || []).map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
              <Field label="Importance"><select data-testid="tl-ev-importance" className={inputCls} value={evModal.form.importance} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, importance: e.target.value } })}>{(meta?.importance_levels || []).map((i) => <option key={i} value={i}>{i}</option>)}</select></Field>
              <Field label="Order (same date)"><input type="number" data-testid="tl-ev-order" className={inputCls} value={evModal.form.order_index} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, order_index: e.target.value } })} /></Field>
            </div>
            <Field label="Summary"><textarea data-testid="tl-ev-summary" className={inputCls} rows={2} value={evModal.form.summary} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, summary: e.target.value } })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Location"><input data-testid="tl-ev-location" className={inputCls} value={evModal.form.location} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, location: e.target.value } })} /></Field>
              <Field label="Analyst notes"><input data-testid="tl-ev-notes" className={inputCls} value={evModal.form.notes} onChange={(e) => setEvModal({ ...evModal, form: { ...evModal.form, notes: e.target.value } })} /></Field>
            </div>
            <Btn data-testid="tl-ev-save" onClick={submitEvent} disabled={saving} className="w-full justify-center">{saving ? "Saving…" : evModal.mode === "create" ? "Add Event" : "Save Event"}</Btn>
          </div>
        )}
      </Modal>

      <Modal open={!!verifyModal} onClose={() => setVerifyModal(null)} title="Set Event Verification">
        {verifyModal && (
          <div className="space-y-4">
            <div className="text-sm text-zinc-300" data-testid="tl-verify-event-title">{verifyModal.ev.title}</div>
            {!verifyModal.ev.has_supporting_evidence && <p className="text-xs text-amber-400">This event has no linked source or Claims &amp; Evidence record. Verifying it will be rejected until evidence is linked.</p>}
            <div className="grid grid-cols-2 gap-2">
              {(meta?.verification_states || []).map((v) => (
                <button key={v} data-testid={`tl-verify-opt-${v}`} onClick={() => submitVerify(v)}
                  className={`px-3 py-2 rounded-sm border text-sm capitalize transition-colors ${VERIFY_COLOR[v]} border-zinc-800 hover:border-zinc-500`}>{v.replace(/_/g, " ")}</button>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!linkModal} onClose={() => setLinkModal(null)} title={`Link ${linkModal ? LINK_MAP[linkModal.type].title : ""}`} wide>
        {linkModal && (
          <div className="space-y-3">
            <div className="max-h-80 overflow-y-auto space-y-1" data-testid="tl-link-options">
              {linkModal.options.length === 0 && <p className="text-xs text-zinc-600">Nothing available. Create one in its own workspace first.</p>}
              {linkModal.options.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm border border-zinc-800 px-3 py-2 rounded-sm cursor-pointer hover:border-zinc-600">
                  <input type="checkbox" checked={linkModal.selected.includes(o.id)} onChange={() => toggleSel(o.id)} />
                  <span className="truncate">{LINK_MAP[linkModal.type].label(o)}</span>
                </label>
              ))}
            </div>
            <Btn data-testid="tl-save-links" onClick={saveLinks} className="w-full justify-center">Save Links</Btn>
          </div>
        )}
      </Modal>

      <Modal open={relatedModal} onClose={() => setRelatedModal(false)} title="Link Related Timelines" wide>
        <div className="space-y-3">
          <div className="max-h-80 overflow-y-auto space-y-1" data-testid="tl-related-options">
            {relatedOptions.length === 0 && <p className="text-xs text-zinc-600">No other timelines available.</p>}
            {relatedOptions.map((o) => (
              <label key={o.id} className="flex items-center gap-2 text-sm border border-zinc-800 px-3 py-2 rounded-sm cursor-pointer hover:border-zinc-600">
                <input type="checkbox" checked={relatedSel.includes(o.id)} onChange={() => toggleRelated(o.id)} />
                <span className="truncate">{o.title} <span className="text-zinc-600 text-xs">({o.status})</span></span>
              </label>
            ))}
          </div>
          <Btn data-testid="tl-save-related" onClick={saveRelated} className="w-full justify-center">Save Related</Btn>
        </div>
      </Modal>
    </div>
  );
}
